import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, ChevronDown, ChevronUp, Filter, Download, MoreHorizontal,
  Columns, ArrowUpDown, X, Loader2, ChevronLeft, ChevronRight,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// EnterpriseTable — Buildium-style table primitive (server-driven).
//
// Props:
//   columns:      [{ id, header, accessor, render?, sortable?, width?, minWidth? }]
//   fetcher:      async ({ page, page_size, search, sort_by, sort_dir, filters }) =>
//                   { items, total, page, page_size }
//   filterDefs:   [{ id, label, options: [{ value, label }] }]  (optional)
//   bulkActions:  [{ id, label, icon?, onConfirm: (ids) => Promise<void> }]
//   rowActions:   [{ id, label, icon?, onClick: (row) => void }]
//   onRowClick:   (row) => void  (optional)
//   rowKey:       string         (defaults to 'id')
//   exportCsv:    boolean        (default true)
//   storageKey:   string         (key to persist column-visibility + page-size)
//   emptyHint:    string
//   testId:       string         (prefix for data-testid)
// ─────────────────────────────────────────────────────────────────────────────
export default function EnterpriseTable({
  columns,
  fetcher,
  filterDefs = [],
  bulkActions = [],
  rowActions = [],
  onRowClick,
  rowKey = 'id',
  exportCsv = true,
  storageKey = 'reva_table_default',
  emptyHint = 'No records yet.',
  testId = 'enterprise-table',
}) {
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => Number(localStorage.getItem(`${storageKey}__page_size`)) || 25);
  useEffect(() => { localStorage.setItem(`${storageKey}__page_size`, String(pageSize)); }, [pageSize, storageKey]);

  // Search + filters
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 280); return () => clearTimeout(t); }, [search]);

  const [filters, setFilters] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Sort
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Column visibility
  const [hidden, setHidden] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`${storageKey}__hidden_cols`) || '[]'); }
    catch { return []; }
  });
  useEffect(() => { localStorage.setItem(`${storageKey}__hidden_cols`, JSON.stringify(hidden)); }, [hidden, storageKey]);
  const [colsOpen, setColsOpen] = useState(false);

  // Selection
  const [selected, setSelected] = useState(new Set());

  // Data
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 25 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inFlight = useRef(0);

  // Fetch
  useEffect(() => {
    const myReq = ++inFlight.current;
    setLoading(true);
    setError(null);
    fetcher({ page, page_size: pageSize, search: debouncedSearch, sort_by: sortBy, sort_dir: sortDir, filters })
      .then(res => {
        if (myReq !== inFlight.current) return;
        setData(res || { items: [], total: 0, page, page_size: pageSize });
      })
      .catch(e => {
        if (myReq !== inFlight.current) return;
        setError(e?.response?.data?.detail || e?.message || 'Failed to load');
      })
      .finally(() => { if (myReq === inFlight.current) setLoading(false); });
  }, [page, pageSize, debouncedSearch, sortBy, sortDir, JSON.stringify(filters)]); // eslint-disable-line

  // Reset to page 1 when filters/search/sort change
  useEffect(() => { setPage(1); setSelected(new Set()); }, [debouncedSearch, sortBy, sortDir, JSON.stringify(filters)]); // eslint-disable-line

  const visibleColumns = useMemo(() => columns.filter(c => !hidden.includes(c.id)), [columns, hidden]);
  const items = data.items || [];
  const total = data.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleSort = (colId) => {
    if (sortBy === colId) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(colId); setSortDir('asc'); }
  };

  const allSelected = items.length > 0 && items.every(r => selected.has(r[rowKey]));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map(r => r[rowKey])));
  };
  const toggleOne = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const downloadCsv = () => {
    const cols = visibleColumns;
    const lines = [cols.map(c => `"${(c.header || '').replace(/"/g, '""')}"`).join(',')];
    items.forEach(r => {
      lines.push(cols.map(c => {
        const v = typeof c.accessor === 'function' ? c.accessor(r) : r[c.accessor || c.id];
        return `"${String(v ?? '').replace(/"/g, '""')}"`;
      }).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${storageKey}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => { setFilters({}); setSearch(''); };

  const activeFilterCount = Object.values(filters).filter(v => v !== null && v !== undefined && v !== '').length;

  return (
    <div className="rounded-xl border border-[#E6EAF0] bg-white overflow-hidden" data-testid={testId}>
      {/* Toolbar */}
      <div className="px-4 py-3 border-b border-[#ECEFF3] flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-[400px]">
          <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#71777E]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search…"
            data-testid={`${testId}-search`}
            className="w-full ps-8 pe-2 py-1.5 text-[13px] border border-[#E6EAF0] rounded-lg focus:border-[#00734A]/40 focus:outline-none"
          />
        </div>

        {filterDefs.length > 0 && (
          <div className="relative">
            <button onClick={() => setFiltersOpen(o => !o)} data-testid={`${testId}-filters-toggle`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12.5px] border border-[#E6EAF0] hover:border-[#0E1320] rounded-lg text-[#0E1320]">
              <Filter className="h-3.5 w-3.5" /> Filters
              {activeFilterCount > 0 && <span className="bg-[#00734A] text-white text-[10px] rounded-full px-1.5">{activeFilterCount}</span>}
            </button>
            {filtersOpen && (
              <div className="absolute z-30 top-full mt-1.5 end-0 w-[280px] rounded-xl border border-[#E6EAF0] bg-white shadow-lg p-3 space-y-2.5"
                data-testid={`${testId}-filters-panel`}>
                {filterDefs.map(f => (
                  <label key={f.id} className="block text-[12px]">
                    <span className="text-[#4A5263]">{f.label}</span>
                    <select
                      value={filters[f.id] || ''}
                      onChange={(e) => setFilters(p => ({ ...p, [f.id]: e.target.value || undefined }))}
                      data-testid={`${testId}-filter-${f.id}`}
                      className="mt-1 w-full h-9 px-2 border border-[#E6EAF0] rounded-md text-[13px]">
                      <option value="">All</option>
                      {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </label>
                ))}
                <div className="flex justify-between pt-1">
                  <button onClick={clearFilters} className="text-[12px] text-[#71777E] hover:underline" data-testid={`${testId}-filters-clear`}>Clear</button>
                  <button onClick={() => setFiltersOpen(false)} className="text-[12px] text-[#00734A] font-medium">Done</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="relative">
          <button onClick={() => setColsOpen(o => !o)} data-testid={`${testId}-cols-toggle`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12.5px] border border-[#E6EAF0] hover:border-[#0E1320] rounded-lg text-[#0E1320]">
            <Columns className="h-3.5 w-3.5" /> Columns
          </button>
          {colsOpen && (
            <div className="absolute z-30 top-full mt-1.5 end-0 w-[240px] rounded-xl border border-[#E6EAF0] bg-white shadow-lg p-2.5"
              data-testid={`${testId}-cols-panel`}>
              {columns.map(c => (
                <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 text-[12.5px] hover:bg-[#F4F6F9] rounded cursor-pointer">
                  <input type="checkbox" checked={!hidden.includes(c.id)}
                    onChange={() => setHidden(h => h.includes(c.id) ? h.filter(x => x !== c.id) : [...h, c.id])} />
                  <span>{c.header}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {exportCsv && (
          <button onClick={downloadCsv} data-testid={`${testId}-export`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12.5px] border border-[#E6EAF0] hover:border-[#0E1320] rounded-lg text-[#0E1320]">
            <Download className="h-3.5 w-3.5" /> Export
          </button>
        )}

        <div className="ms-auto text-[12px] text-[#71777E] reva-mono">
          {loading ? 'Loading…' : `${total.toLocaleString()} ${total === 1 ? 'record' : 'records'}`}
        </div>
      </div>

      {/* Bulk-action bar */}
      {selected.size > 0 && (
        <div className="px-4 py-2 bg-[#00734A]/[0.06] border-b border-[#00734A]/20 flex items-center gap-2 text-[12.5px]" data-testid={`${testId}-bulk-bar`}>
          <span className="font-medium text-[#0E1320]">{selected.size} selected</span>
          <button onClick={() => setSelected(new Set())} className="ms-2 text-[#71777E] hover:text-[#0E1320] inline-flex items-center gap-1"><X size={12} /> Clear</button>
          <div className="ms-auto inline-flex items-center gap-1.5">
            {bulkActions.map(a => (
              <button key={a.id} onClick={async () => { await a.onConfirm(Array.from(selected)); setSelected(new Set()); }}
                data-testid={`${testId}-bulk-${a.id}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] bg-white border border-[#E6EAF0] hover:border-[#0E1320] rounded-md">
                {a.icon} {a.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 320px)' }}>
        <table className="w-full text-[13px]" data-testid={`${testId}-table`}>
          <thead className="bg-[#FBFCFD] sticky top-0 z-10 border-b border-[#ECEFF3]">
            <tr>
              <th className="w-10 text-center">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid={`${testId}-select-all`} />
              </th>
              {visibleColumns.map(c => (
                <th key={c.id}
                  style={{ width: c.width, minWidth: c.minWidth || 100 }}
                  className="text-start px-3 py-2.5 text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E] font-semibold">
                  {c.sortable === false ? (
                    <span>{c.header}</span>
                  ) : (
                    <button onClick={() => toggleSort(c.id)} data-testid={`${testId}-sort-${c.id}`}
                      className="inline-flex items-center gap-1 hover:text-[#0E1320]">
                      {c.header}
                      {sortBy === c.id ? (sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />) : <ArrowUpDown size={10} className="opacity-40" />}
                    </button>
                  )}
                </th>
              ))}
              {rowActions.length > 0 && <th className="w-10" />}
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr><td colSpan={visibleColumns.length + 2} className="text-center py-12 text-[#71777E]"><Loader2 className="inline h-4 w-4 animate-spin me-2" /> Loading…</td></tr>
            ) : error ? (
              <tr><td colSpan={visibleColumns.length + 2} className="text-center py-12 text-red-600">{error}</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={visibleColumns.length + 2} className="text-center py-12 text-[#71777E]">{emptyHint}</td></tr>
            ) : items.map(r => (
              <tr key={r[rowKey]}
                onClick={() => onRowClick?.(r)}
                data-testid={`${testId}-row-${r[rowKey]}`}
                className={`border-b border-[#F1F4F7] hover:bg-[#F7FBF9] ${onRowClick ? 'cursor-pointer' : ''}`}>
                <td className="text-center" onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(r[rowKey])} onChange={() => toggleOne(r[rowKey])} data-testid={`${testId}-select-${r[rowKey]}`} />
                </td>
                {visibleColumns.map(c => {
                  const v = typeof c.accessor === 'function' ? c.accessor(r) : r[c.accessor || c.id];
                  return (
                    <td key={c.id} className="px-3 py-2.5 text-[#0E1320]">
                      {c.render ? c.render(v, r) : (v ?? '—')}
                    </td>
                  );
                })}
                {rowActions.length > 0 && (
                  <td onClick={e => e.stopPropagation()} className="text-center">
                    <RowMenu actions={rowActions} row={r} testid={testId} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 py-2.5 border-t border-[#ECEFF3] flex items-center gap-3 text-[12px] text-[#4A5263]">
        <label className="inline-flex items-center gap-1.5">
          Rows
          <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} data-testid={`${testId}-page-size`}
            className="h-7 px-1.5 border border-[#E6EAF0] rounded text-[12px]">
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <div className="ms-auto inline-flex items-center gap-1">
          <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid={`${testId}-prev`}
            className="p-1.5 rounded hover:bg-[#F4F6F9] disabled:opacity-40 disabled:cursor-not-allowed">
            <ChevronLeft size={14} />
          </button>
          <span className="reva-mono">Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid={`${testId}-next`}
            className="p-1.5 rounded hover:bg-[#F4F6F9] disabled:opacity-40 disabled:cursor-not-allowed">
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function RowMenu({ actions, row, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button onClick={() => setOpen(o => !o)} className="p-1.5 hover:bg-[#F4F6F9] rounded" data-testid={`${testid}-rowmenu-${row.id}`}>
        <MoreHorizontal size={14} />
      </button>
      {open && (
        <>
          <button onClick={() => setOpen(false)} className="fixed inset-0 z-30 cursor-default" style={{ background: 'transparent' }} tabIndex={-1} />
          <div className="absolute z-40 end-0 top-full mt-1 w-[180px] rounded-lg border border-[#E6EAF0] bg-white shadow-lg py-1">
            {actions.map(a => (
              <button key={a.id} onClick={() => { setOpen(false); a.onClick(row); }}
                data-testid={`${testid}-rowmenu-${row.id}-${a.id}`}
                className="w-full text-start px-3 py-1.5 text-[12.5px] hover:bg-[#F4F6F9] inline-flex items-center gap-2">
                {a.icon} {a.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
